// The notes of a MIDI song as a timeline (issue #3718): every note with its voice, pitch, velocity
// and its start and length in seconds at the file's own tempo map. Pure: no Web Audio, no DOM, so
// the Web Audio song scheduler can be unit-tested against it. The MP3 generator in instrument.ts
// reads the same tempo map and the same voice rules, so both playback paths agree on when a note
// sounds and whose it is.

import { MidiData, MidiEvent, MidiSetTempoEvent, MidiTimeSignatureEvent, MidiTrackNameEvent } from 'midi-file';
import { Voice } from '../../../common/src/model.ts';

const microsecondsPerMinute = 60_000_000;
const maxMidiNoteVelocity = 127;
export const defaultFileTempo = 100; // a file without a tempo event plays at this, as the MP3 path always has
const defaultBeatQuarterNotes = 1; // a file without a time signature is counted in quarter notes

export interface TempoEntry {
  tick: number; // absolute tick where this tempo takes effect
  tempo: number; // BPM
}

// The tempo changes of the file's metadata track (the first track), scaled by `tempoScale`; a file
// without any gets the default tempo from tick 0
export function getTempoMap(midi: MidiData, tempoScale: number): TempoEntry[] {
  const metadataTrack = midi.tracks[0] ?? [];
  const entries: TempoEntry[] = [];
  let absoluteTick = 0;
  for (const event of metadataTrack) {
    absoluteTick += event.deltaTime;
    if (event.type === 'setTempo') {
      const baseTempo = Math.round(microsecondsPerMinute / (event as MidiSetTempoEvent).microsecondsPerBeat);
      entries.push({ tick: absoluteTick, tempo: baseTempo * tempoScale });
    }
  }
  if (entries.length === 0) {
    entries.push({ tick: 0, tempo: defaultFileTempo * tempoScale });
  }
  return entries;
}

// The channel fallback is intentionally maintained for maximum compatibility with arbitrary MIDI
// files that lack voice track names, such as author-provided or notation-software-exported files
// (e.g. the Tanglewood Hymnal LilyPond exports, which use channels 0-3 in SATB order).
export function getVoiceForChannel(channel: number): Voice {
  switch (channel) {
    case 0:
      return Voice.Soprano;
    case 1:
      return Voice.Alto;
    case 2:
      return Voice.Tenor;
    case 3:
      return Voice.Bass;
    case 4:
    default:
      return Voice.Piano;
  }
}

// The voice a track names itself for, lower-cased as the Voice values are; undefined without a name
export function getTrackVoice(events: MidiEvent[]): Voice | undefined {
  return (events.find(({ type }) => type === 'trackName') as MidiTrackNameEvent | undefined)?.text?.toLowerCase() as
    Voice | undefined;
}

// The beat as the song is counted, in quarter notes, from the first time signature of the metadata
// track (issue #3843): the denominator's note in a simple meter (a half note in 2/2, a quarter in
// 3/4), the dotted quarter in a compound one (6/8, 9/8, 12/8). This is the pulse a congregation
// feels, which the quarter-note tempo alone does not say: 2/2 at 150 is a slower pulse than 3/4
// at 90.
export function getBeatQuarterNotes(midi: MidiData): number {
  const metadataTrack = midi.tracks[0] ?? [];
  const timeSignature = metadataTrack.find(({ type }) => type === 'timeSignature') as
    MidiTimeSignatureEvent | undefined;
  if (!timeSignature || !(timeSignature.denominator > 0)) {
    return defaultBeatQuarterNotes;
  }
  const { numerator, denominator } = timeSignature;
  if (denominator === 8 && numerator % 3 === 0) {
    return 1.5; // compound meter: felt in dotted quarters
  }
  return 4 / denominator;
}

export interface MidiNote {
  voice: Voice;
  noteNumber: number; // the MIDI note number before any transposition
  velocity: number; // 0..1
  startSec: number; // from the start of the song, at the file's tempo map
  durationSec: number; // at the file's tempo map; a note never turned off runs to the end of its track
}

export interface MidiSong {
  notes: MidiNote[]; // in order of start
  durationSec: number; // the end of the last track, at the file's tempo map
  fileTempo: number; // the first tempo of the file (BPM)
  endTempo: number; // the last tempo of the file (BPM): the one the song ends in
  beatQuarterNotes: number; // the beat as the song is counted, in quarter notes (getBeatQuarterNotes)
  ticksPerBeat: number;
  voices: Voice[]; // the distinct track names of the tracks that have notes, in file order
}

// Lays the file's tracks out in seconds at the file's own tempo map. Undefined when the header has
// no ticksPerBeat (SMPTE timing), which no song of ours uses.
export function getMidiSong(midi: MidiData): MidiSong | undefined {
  const { ticksPerBeat } = midi.header;
  if (!ticksPerBeat) {
    return;
  }
  const tempoMap = getTempoMap(midi, 1);
  const notes: MidiNote[] = [];
  let durationSec = 0;
  const voices: Voice[] = [];
  for (const track of midi.tracks) {
    const hasNotes = track.some((event) => event.type === 'noteOn');
    if (!hasNotes) {
      continue;
    }
    const trackVoice = getTrackVoice(track);
    if (trackVoice && !voices.includes(trackVoice)) {
      voices.push(trackVoice);
    }
    const { notes: trackNotes, durationSec: trackDurationSec } = getNotesFromTrack(
      track,
      ticksPerBeat,
      tempoMap,
      trackVoice,
    );
    notes.push(...trackNotes);
    durationSec = Math.max(durationSec, trackDurationSec);
  }
  notes.sort((lhs, rhs) => lhs.startSec - rhs.startSec);
  return {
    notes,
    durationSec,
    fileTempo: tempoMap[0].tempo,
    endTempo: tempoMap[tempoMap.length - 1].tempo,
    beatQuarterNotes: getBeatQuarterNotes(midi),
    ticksPerBeat,
    voices,
  };
}

// Walks one track, converting ticks to seconds through the tempo map (the same walk the MP3
// generator makes). A note still on at the end of the track ends with the track.
function getNotesFromTrack(
  events: MidiEvent[],
  ticksPerBeat: number,
  tempoMap: TempoEntry[],
  trackVoice: Voice | undefined,
): { notes: MidiNote[]; durationSec: number } {
  const notes: MidiNote[] = [];
  const activeNotes = new Map<number, MidiNote>();
  let currentTimeSec = 0;
  let absoluteTick = 0;
  let tempoMapIndex = 0;
  for (const event of events) {
    let remainingTicks = event.deltaTime;
    while (tempoMapIndex < tempoMap.length - 1 && remainingTicks > 0) {
      const nextTempoTick = tempoMap[tempoMapIndex + 1].tick;
      const ticksUntilNextTempo = nextTempoTick - absoluteTick;
      if (remainingTicks < ticksUntilNextTempo) {
        break;
      }
      currentTimeSec += (ticksUntilNextTempo / ticksPerBeat / tempoMap[tempoMapIndex].tempo) * 60;
      absoluteTick = nextTempoTick;
      remainingTicks -= ticksUntilNextTempo;
      tempoMapIndex++;
    }
    currentTimeSec += (remainingTicks / ticksPerBeat / tempoMap[tempoMapIndex].tempo) * 60;
    absoluteTick += remainingTicks;
    if (event.type !== 'noteOn' && event.type !== 'noteOff') {
      continue;
    }
    const { channel, noteNumber, velocity } = event;
    // a note-on with velocity 0 is a note-off by convention
    if (event.type === 'noteOn' && velocity > 0) {
      if (activeNotes.has(noteNumber)) {
        continue; // a second note-on before the note-off: the first one stands (as in the MP3 path)
      }
      const note: MidiNote = {
        voice: trackVoice ?? getVoiceForChannel(channel),
        noteNumber,
        velocity: velocity / maxMidiNoteVelocity,
        startSec: currentTimeSec,
        durationSec: 0,
      };
      notes.push(note);
      activeNotes.set(noteNumber, note);
      continue;
    }
    const note = activeNotes.get(noteNumber);
    if (!note) {
      continue; // a note-off without its note-on
    }
    note.durationSec = currentTimeSec - note.startSec;
    activeNotes.delete(noteNumber);
  }
  for (const note of activeNotes.values()) {
    note.durationSec = currentTimeSec - note.startSec;
  }
  return { notes, durationSec: currentTimeSec };
}
